import { useCallback, useMemo } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm, useWatch } from 'react-hook-form'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import {
  AddColorFormSchema,
  type AddSpendColorValues,
} from '@/dialogs/FanSpendColorDialog/validation.ts'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import type { DialogProps } from '@/dialogs/types'
import { openDialog } from '@/models/dialogs.ts'
import type { AddColorBody } from '@/modules/display-settings/service/inbox-indication-color-service.ts'
import type {
  ColorWithIndex,
  ColorWithPrice,
} from '@/modules/display-settings/utils.ts'

interface Props extends DialogProps {
  mode: 'add' | 'edit'
  defaultValues?: ColorWithIndex
  onAddColor: (newColor: AddColorBody) => void
  onUpdateColor: (index: number, color: Partial<ColorWithPrice>) => void
}

export const FanSpendColorDialog = ({
  mode,
  onAddColor,
  onUpdateColor,
  onClose,
  defaultValues,
  isOpen,
}: Props) => {
  const initialValues: AddSpendColorValues = useMemo(
    () => ({
      price: defaultValues?.price ?? 0,
      colorHex: defaultValues?.colorHex || '#1c83e3',
      backgroundColorHex: defaultValues?.backgroundColorHex || '',
    }),
    [defaultValues],
  )

  const addForm = useForm<AddSpendColorValues>({
    defaultValues: initialValues,
    resolver: zodResolver(AddColorFormSchema),
  })

  const { colorHex, backgroundColorHex } = useWatch({
    control: addForm.control,
    defaultValue: initialValues,
    exact: false,
  })

  const openColorPicker = (color: 'color' | 'inbox-color') => {
    openDialog({
      type: DIALOG_TYPE.COLOR_PICKER,
      onColorChange: (newColor) => {
        if (color === 'color') {
          addForm.setValue('colorHex', newColor)
        } else if (color === 'inbox-color') {
          addForm.setValue('backgroundColorHex', newColor)
        }
      },
      oldColor: color === 'color' ? colorHex : backgroundColorHex || '',
    })
  }

  const handleColorSubmit = useCallback(
    async (values: AddSpendColorValues) => {
      const obj: Partial<ColorWithPrice> = {
        // @ts-ignore
        id: defaultValues?.id || null,
        ...values,
      }

      if (mode === 'add') {
        await onAddColor({
          ...values,
          backgroundColorHex:
            values.backgroundColorHex === '' ? null : values.backgroundColorHex,
        })
      } else if (mode === 'edit') {
        await onUpdateColor(defaultValues!.index, obj)
      }

      onClose()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onAddColor, onUpdateColor, mode],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <Dialog.Header>
        <Dialog.Title className="text-center">
          {mode === 'add' ? 'New Fan Spend Color' : 'Edit Fan Spend Color'}
        </Dialog.Title>

        <Dialog.Description className="text-center">
          Highlight your fans in different colors depending on their spend
        </Dialog.Description>
      </Dialog.Header>

      <FormProvider {...addForm}>
        <form
          onSubmit={addForm.handleSubmit(handleColorSubmit)}
          className="mt-6 flex flex-col gap-6"
        >
          <FormField
            name="price"
            label="Spend"
            type="number"
            step="0.01"
            prefix={<span className="text-secondary-300">$</span>}
          />

          <div className="flex gap-4">
            <FormField
              name="colorHex"
              label="Color"
              placeholder="Select Color"
              type="text"
              prefix={
                <div
                  className="h-5 w-5 cursor-pointer rounded-full border"
                  style={{ backgroundColor: colorHex }}
                  onClick={() => openColorPicker('color')}
                />
              }
            />

            <FormField
              name="backgroundColorHex"
              label="Inbox Color"
              placeholder="Select Color"
              type="text"
              prefix={
                <div
                  className="h-5 w-5 cursor-pointer rounded-full border"
                  onClick={() => openColorPicker('inbox-color')}
                  style={{
                    backgroundColor: backgroundColorHex || '',
                  }}
                />
              }
            />
          </div>

          <Dialog.Footer>
            <Button
              className="w-full"
              variant="outline"
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="w-full"
              isLoading={addForm.formState.isSubmitting}
            >
              Save
            </Button>
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
